import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLoginMutation } from './authApi';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/button';
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';

type LoginFormData = {
  email: string;
  password: string;
};

export const LoginForm = () => {
  const [login, { isLoading, error: apiError }] = useLoginMutation();
  const [localError, setLocalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    watch,
  } = useForm<LoginFormData>({
    mode: 'onChange',
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');

  const isFormValid = emailValue?.length > 0 && passwordValue?.length > 0;

  const onSubmit = async (data: LoginFormData) => {
    setLocalError(null);
    try {
      await login(data).unwrap();
      // authSlice.extraReducers handles token storage and state update - App.tsx now handles redirect
    } catch (err) {
      console.error('Failed to login:', err);
      setLocalError('Incorrect email or password.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-5">
      <div className="flex flex-col gap-8">
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            {...register('email', {
              required: 'Required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
              }
            })}
            error={touchedFields.email ? errors.email?.message : undefined}
            autoComplete="email"
            className="!w-full"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
        >
          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            {...register('password', {
              required: 'Required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' }
            })}
            error={touchedFields.password ? errors.password?.message : undefined}
            autoComplete="current-password"
            className="!w-full"
          />
        </motion.div>
      </div>



      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          opacity: { duration: 0.4, delay: 0.25 },
          y: { duration: 0.4, delay: 0.25 },
          default: { type: "spring", stiffness: 400, damping: 15 }
        }}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        className="pt-1"
      >
        <Button
          type="submit"
          disabled={!isFormValid || isLoading}
          className="group w-full !bg-gradient-to-r !from-[#2C9944] !to-[#4CAF50] hover:!from-[#227835] hover:!to-[#3d8b40] !shadow-lg hover:!shadow-xl !text-white !h-[56px] !rounded-[12px] transition-all duration-300 disabled:!from-[#BFE4C7] disabled:!to-[#BFE4C7] disabled:!opacity-100 border-none"
        >
          <span className="flex items-center justify-center gap-2 text-[16px] font-semibold tracking-wide">
            {isLoading ? 'Signing in...' : 'Login'}
            {!isLoading && (
              <ArrowRight className="w-5 h-5 opacity-80 group-hover:translate-x-1 transition-transform duration-300" />
            )}
          </span>
        </Button>
      </motion.div>

      <div className="relative flex py-2 items-center">
        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
        <span className="flex-shrink mx-4 text-gray-400 text-xs uppercase font-medium">Or</span>
        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          type="button"
          onClick={() => {
            const apiBase = import.meta.env.VITE_CMS_API_BASE_URL || 'http://localhost:3000';
            window.location.href = `${apiBase}/v1/sso/authorize-creator`;
          }}
          className="w-full !bg-slate-100 dark:!bg-slate-800 hover:!bg-slate-200 dark:hover:!bg-slate-700 !text-gray-700 dark:!text-gray-200 border border-gray-200 dark:border-gray-700 !h-[56px] !rounded-[12px] transition-all duration-300 flex items-center justify-center gap-2 font-semibold text-[16px] tracking-wide"
        >
          <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
          Sign in with SSO
        </Button>
      </motion.div>

      {(apiError || localError) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="text-red-500 text-center w-full text-sm bg-red-50/50 border border-red-100 p-2.5 rounded-lg"
        >
          {localError || 'Incorrect email or password.'}
        </motion.div>
      )}
    </form>
  );
};

